/**
 * Strongly typed models for user session management
 */
export interface UserInfo {
  id: string;
  email: string;
  name: string;
  givenName?: string;
  familyName?: string;
  userType?: string;
  customerId?: string;
  customerName?: string;
  permissions?: string[];
}

export interface ImpersonatingUserInfo {
  id: string;
  email: string;
  name: string;
}

export interface SessionInfo {
  logoutUrl: string;
  expiresIn: number;
  expiresAt: Date;
  sessionState: string;
  sessionId?: string;
}

export interface UserSessionInfo {
  currentUser: UserInfo;
  impersonatingUser?: ImpersonatingUserInfo;
  session: SessionInfo;
}

/**
 * Service class for managing user session information
 * Follows clean architecture principles with separation of concerns
 */
import { ClaimKeys } from "./claim-keys";
import { UserPermissionService } from "./user-permission-service";

export class UserSessionService {
  private static readonly SESSION_STORAGE_KEY = "userSessionInfo";
  private static readonly SESSION_USER_KEY = "SessionUser";
  private static readonly TOKEN_KEY = "Token";
  private static readonly CUSTOMER_ID_KEY = "CustomerId";
  private static readonly INTERNAL_USER_TYPE = "internal";

  /**
   * Parse claims and create a strongly typed UserSessionInfo object
   * @param claims - Raw claims array from identity provider
   * @returns UserSessionInfo object
   */
  static parseClaimsToUserSession(claims: any[]): UserSessionInfo {
    // Extract impersonation information
    const impUid = this.findClaimValue(claims, ClaimKeys.IMPERSONATED_USER_ID);
    const actEmail = this.findClaimValue(claims, ClaimKeys.ACTING_USER_EMAIL);
    const actName = this.findClaimValue(claims, ClaimKeys.ACTING_USER_NAME);

    // Extract user information
    const sub = this.findClaimValue(claims, ClaimKeys.SUB);
    const email = this.findClaimValue(claims, ClaimKeys.EMAIL);
    const name = this.findClaimValue(claims, ClaimKeys.NAME);
    const givenName = this.findClaimValue(claims, ClaimKeys.GIVEN_NAME);
    const familyName = this.findClaimValue(claims, ClaimKeys.FAMILY_NAME);
    const userType = this.findClaimValue(claims, ClaimKeys.USER_TYPE);
    const customerId = this.findClaimValue(claims, ClaimKeys.CUSTOMER_ID);
    const customerName = this.findClaimValue(claims, ClaimKeys.CUSTOMER_NAME);

    // Extract session information
    const logoutUrl = this.findClaimValue(claims, ClaimKeys.LOGOUT_URL);
    const sessionExpiresIn = parseInt(
      this.findClaimValue(claims, ClaimKeys.SESSION_EXPIRES_IN) || "0",
      10
    );
    const sessionState = this.findClaimValue(claims, ClaimKeys.SESSION_STATE);
    const sessionId = this.findClaimValue(claims, ClaimKeys.SESSION_ID);

    // Determine if impersonation is active and set current user
    const isImpersonating = !!impUid;
    const currentUserId = isImpersonating ? impUid : sub;

    const currentUser: UserInfo = {
      id: currentUserId || "",
      email: email || "",
      name: name || "",
      givenName: givenName || undefined,
      familyName: familyName || undefined,
      userType: userType || undefined,
      customerId: customerId || undefined,
      customerName: customerName || undefined,
    };

    const session: SessionInfo = {
      logoutUrl: logoutUrl || "",
      sessionId: sessionId || undefined,
      expiresIn: sessionExpiresIn,
      expiresAt: new Date(Date.now() + sessionExpiresIn * 1000),
      sessionState: sessionState || "",
    };

    let userSessionInfo: UserSessionInfo;

    if (isImpersonating) {
      // Impersonation is active
      const impersonatingUser: ImpersonatingUserInfo = {
        id: sub || "",
        email: actEmail || "",
        name: actName || "",
      };

      userSessionInfo = {
        currentUser,
        impersonatingUser,
        session,
      };
    } else {
      // No impersonation - the sub user is the current user
      userSessionInfo = {
        currentUser,
        session,
      };
    }

    return userSessionInfo;
  }

  /**
   * Store user session information in session storage
   * @param userSession - UserSessionInfo to store
   */
  static storeUserSession(userSession: UserSessionInfo): void {
    try {
      const serializedSession = JSON.stringify(userSession, (key, value) => {
        // Convert Date objects to ISO strings for storage
        if (value instanceof Date) {
          return value.toISOString();
        }
        return value;
      });

      const serializedSessionUser = JSON.stringify({
        ApplicationPermissionScopes:
          userSession?.currentUser?.permissions ?? [],
        Internal: this.isInternalUserType(userSession?.currentUser?.userType),
        Enabled: userSession?.currentUser ? true : false,
        Id: 1,
      });

      const actor = userSession?.impersonatingUser ?? userSession?.currentUser;
      const tokenPayload = {
        Email: actor?.email ?? "",
        Id: actor?.id ?? "",
        Expired: false,
      };

      sessionStorage.setItem(this.SESSION_STORAGE_KEY, serializedSession);
      sessionStorage.setItem(this.SESSION_USER_KEY, serializedSessionUser);
      sessionStorage.setItem(this.TOKEN_KEY, JSON.stringify(tokenPayload));
      sessionStorage.setItem(
        this.CUSTOMER_ID_KEY,
        userSession?.currentUser?.customerId ?? ""
      );
    } catch (error) {
      console.error("Error storing user session in session storage:", error);
      throw new Error("Failed to store user session");
    }
  }

  /**
   * Retrieve user session information from session storage
   * @returns UserSessionInfo or null if not found
   */
  static getUserSession(): UserSessionInfo | null {
    try {
      const sessionData = sessionStorage.getItem(this.SESSION_STORAGE_KEY);
      if (!sessionData) return null;

      const parsed = JSON.parse(sessionData);
      // Convert ISO date strings back to Date objects
      if (parsed.session && parsed.session.expiresAt) {
        parsed.session.expiresAt = new Date(parsed.session.expiresAt);
      }
      return parsed;
    } catch (error) {
      console.error(
        "Error retrieving user session from session storage:",
        error
      );
      return null;
    }
  }

  /**
   * Clear user session information from session storage
   */
  static clearUserSession(): void {
    try {
      sessionStorage.removeItem(this.SESSION_STORAGE_KEY);
      sessionStorage.removeItem(this.SESSION_USER_KEY);
      sessionStorage.removeItem(this.CUSTOMER_ID_KEY);
      sessionStorage.removeItem(this.TOKEN_KEY);
    } catch (error) {
      console.error("Error clearing user session from session storage:", error);
    }
  }

  /**
   * Check if user is currently being impersonated
   * @returns true if impersonation is active
   */
  static isImpersonating(): boolean {
    const session = this.getUserSession();
    return !!session?.impersonatingUser;
  }

  /**
   * Get the current active user (impersonated user if impersonation is active)
   * @returns UserInfo of the current active user
   */
  static getCurrentUser(): UserInfo | null {
    const session = this.getUserSession();
    return session?.currentUser || null;
  }

  /**
   * Get the impersonating user (only available when impersonation is active)
   * @returns ImpersonatingUserInfo or null
   */
  static getImpersonatingUser(): ImpersonatingUserInfo | null {
    const session = this.getUserSession();
    return session?.impersonatingUser || null;
  }

  /**
   * Get session information
   * @returns SessionInfo or null
   */
  static getSessionInfo(): SessionInfo | null {
    const session = this.getUserSession();
    return session?.session || null;
  }

  /**
   * Load user session from the identity server, enrich with permissions, and store in session storage.
   * This is the single entry point for bootstrapping a user session.
   * @param baseIdentityUrl - Base URL of the identity server
   * @param csrfToken - CSRF token for the request
   * @returns UserSessionInfo or null if the user is not authenticated
   */
  static async loadAndStoreUserSession(
    baseIdentityUrl: string,
    authServiceUrl: string,
    csrfToken: string | null
  ): Promise<UserSessionInfo | null> {
    const headers = new Headers();
    headers.append("X-CSRF", csrfToken || "1");

    const response = await fetch(`${baseIdentityUrl}/session/user`, {
      method: "GET",
      headers,
      redirect: "follow",
      credentials: "include",
    });

    if (response.status === 401) {
      return null;
    }

    const claims = await response.json();
    const userSessionInfo = this.parseClaimsToUserSession(claims);

    if (
      userSessionInfo?.currentUser?.id &&
      userSessionInfo?.currentUser?.customerId
    ) {
      userSessionInfo.currentUser.permissions =
        await UserPermissionService.fetchPermissions(
          authServiceUrl,
          userSessionInfo.currentUser.id,
          userSessionInfo.currentUser.customerId,
          csrfToken
        );
    }

    this.storeUserSession(userSessionInfo);
    return userSessionInfo;
  }

  /**
   * Get the current user's permissions from session storage
   * @returns Array of permission strings or null if not found
   */
  static getCurrentUserPermissions(): string[] | null {
    const session = this.getUserSession();
    return session?.currentUser?.permissions || null;
  }

  /**
   * Helper method to find a claim value by type
   * @param claims - Array of claims
   * @param claimType - Type of claim to find
   * @returns Claim value as string or undefined
   */
  private static findClaimValue(
    claims: any[],
    claimType: string
  ): string | undefined {
    const claim = claims.find((c) => c.type === claimType);
    return claim?.value?.toString();
  }

  private static isInternalUserType(userType?: string | null): boolean {
    return userType?.trim().toLowerCase() === this.INTERNAL_USER_TYPE;
  }
}
